import type { ITableCell, ITableSettings } from "@library-elements/table";
import { type RootState, store } from "@store";
import { setTableActiveCellsIds } from "@store/story";
import {
  getTableActiveCellsIds,
  getTableActiveStartCellId,
} from "@store/story/selectors";
import {
  copyManyTableCellBorders,
  copyTableCellBorders,
} from "./copy-table-cell-borders";
import { editTableEntity } from "./edit-table-entity";
import { makeTableEmptyCell } from "./make-table-empty-cell";
import {
  adjustMergesForRowDelete,
  adjustMergesForRowInsert,
  markInsertedCellsAsMerged,
} from "./table-cell-merge-actions";

export interface IOpts {
  cellId?: string;
  entityId?: string;
  // Selection snapshot; defaults to the store's active cells.
  cellsIds?: string[];
}

export const tableRemoveActiveCellRow = (opts?: IOpts) => {
  editTableEntity(
    (draft, state) => {
      const { tableCols } = draft;
      const rows = new Set(
        getTableDeleteTargetIdxs(draft.tableData, state, opts).map((idx) =>
          Math.floor(idx / tableCols),
        ),
      );
      if (rows.size === 0) return;

      // Bottom-up, so the remaining target rows keep their indexes.
      for (const row of Array.from(rows).sort((a, b) => b - a)) {
        removeRowFromDraft(draft, row);
      }
    },
    { entityId: opts?.entityId },
  );

  store.dispatch(setTableActiveCellsIds([]));
};

/**
 * Cell indexes a row/column delete acts on: the whole selection when the
 * anchor cell is part of it, otherwise just the anchor (right-click outside
 * the selection, or the toolbar's fallback cell).
 */
export const getTableDeleteTargetIdxs = (
  tableData: ITableCell[],
  state: RootState,
  opts?: IOpts,
): number[] => {
  const anchorId = opts?.cellId || getTableActiveStartCellId(state);
  const selected = opts?.cellsIds ?? getTableActiveCellsIds(state);
  const useSelection =
    selected.length > 0 && (!anchorId || selected.includes(anchorId));
  const wanted = new Set(useSelection ? selected : [anchorId]);
  const idxs: number[] = [];
  tableData.forEach((cell, idx) => {
    if (wanted.has(cell.id)) idxs.push(idx);
  });
  return idxs;
};

export const removeRowFromDraft = (draft: ITableSettings, cellRow: number) => {
  const { tableData, tableCols } = draft;
  const idxStart = cellRow * tableCols;

  // Adjust merge regions before removing the row
  adjustMergesForRowDelete(draft, cellRow);

  for (let i = 0; i < tableCols; i++) {
    const aboveCell = tableData[idxStart + i - tableCols];
    if (aboveCell) {
      const belowCell = tableData[idxStart + i + tableCols];
      if (belowCell) {
        copyTableCellBorders(belowCell, "Top", aboveCell, "Bottom");
      }
    }
  }

  tableData.splice(cellRow * tableCols, tableCols);
  draft.tableRows = Math.max(0, draft.tableRows - 1);

  if (draft.tableFrozenRowsCount && draft.tableFrozenRowsCount > 0) {
    if (cellRow < draft.tableFrozenRowsCount) {
      draft.tableFrozenRowsCount -= 1;
    }
    draft.tableFrozenRowsCount = Math.min(
      draft.tableFrozenRowsCount,
      Math.max(0, draft.tableRows - 1),
    );
  }
};

interface IAddRowToEntityProps {
  draft: ITableSettings;
  state: RootState;
  addAfter: boolean;
  cellId?: string;
}

export const tableAddRowToEntity = ({
  cellId,
  draft,
  state,
  addAfter,
}: IAddRowToEntityProps) => {
  const activeCellId = cellId || getTableActiveStartCellId(state);
  const { tableData, tableCols } = draft;

  const activeCellIdx = tableData.findIndex((el) => el.id === activeCellId);
  if (activeCellIdx < 0) return;

  const cellRow = Math.floor(activeCellIdx / tableCols);
  const idxStart = cellRow * tableCols;

  // Adjust merge regions before inserting the row
  const insertRow = cellRow + (addAfter ? 1 : 0);
  adjustMergesForRowInsert(draft, insertRow);

  const newCells: ITableCell[] = [];
  for (let i = 0; i < tableCols; i++) {
    newCells.push(makeTableEmptyCell());
  }

  for (let i = 0; i < newCells.length; i++) {
    const currentCell = tableData[idxStart + i];
    const newCell = newCells[i];
    if (!addAfter) {
      copyTableCellBorders(newCell, "Bottom", currentCell, "Top");

      copyManyTableCellBorders(newCell, currentCell, ["Right", "Left", "Top"]);
      const aboveCell = tableData[idxStart + i - tableCols];
      if (aboveCell) {
        copyTableCellBorders(newCell, "Top", aboveCell, "Bottom");
      }
    } else {
      copyTableCellBorders(newCell, "Top", currentCell, "Bottom");

      copyManyTableCellBorders(newCell, currentCell, [
        "Right",
        "Left",
        "Bottom",
      ]);
      const belowCell = tableData[idxStart + i + tableCols];
      if (belowCell) {
        copyTableCellBorders(newCell, "Bottom", belowCell, "Top");
      }
    }
  }
  tableData.splice((cellRow + (addAfter ? 1 : 0)) * tableCols, 0, ...newCells);
  draft.tableRows += 1;

  if (draft.tableFrozenRowsCount && insertRow < draft.tableFrozenRowsCount) {
    draft.tableFrozenRowsCount = Math.min(
      draft.tableFrozenRowsCount + 1,
      Math.max(0, draft.tableRows - 1),
    );
  }

  // Mark newly inserted cells that fall inside merge regions
  markInsertedCellsAsMerged(draft);
};

const tableAddRow = (addAfter: boolean, opts?: IOpts) => {
  editTableEntity(
    (draft, state) => {
      tableAddRowToEntity({
        addAfter,
        draft,
        state,
        cellId: opts?.cellId,
      });
    },
    { entityId: opts?.entityId },
  );
};

export const tableAddRowAfterActiveCell = (opts?: IOpts) => {
  tableAddRow(true, opts);
};

export const tableAddRowBeforeActiveCell = (opts?: IOpts) => {
  tableAddRow(false, opts);
};

export const tableDuplicateRow = (opts?: IOpts) => {
  editTableEntity(
    (draft, state) => {
      const activeCellId = opts?.cellId || getTableActiveStartCellId(state);
      const { tableData, tableCols } = draft;

      const activeCellIdx = tableData.findIndex((el) => el.id === activeCellId);
      if (activeCellIdx < 0) return;

      const cellRow = Math.floor(activeCellIdx / tableCols);
      const idxStart = cellRow * tableCols;

      // Adjust merge regions before inserting the row
      adjustMergesForRowInsert(draft, cellRow + 1);

      const newCells: ITableCell[] = [];
      for (let i = 0; i < tableCols; i++) {
        const sourceCell = tableData[idxStart + i];
        newCells.push({
          ...sourceCell,
          id: makeTableEmptyCell().id,
          mergedInto: undefined,
          colSpan: 1,
          rowSpan: 1,
        });
      }

      tableData.splice((cellRow + 1) * tableCols, 0, ...newCells);
      draft.tableRows += 1;

      if (draft.tableFrozenRowsCount && cellRow + 1 < draft.tableFrozenRowsCount) {
        draft.tableFrozenRowsCount = Math.min(
          draft.tableFrozenRowsCount + 1,
          Math.max(0, draft.tableRows - 1),
        );
      }

      markInsertedCellsAsMerged(draft);
    },
    { entityId: opts?.entityId },
  );
};
